import { Loader2 } from "lucide-react";
import type React from "react";
import { Suspense } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "~/components/ui";
import {
  useRelationTypeCreate,
  useRelationTypeEdit,
  useRelationTypesModelContext,
} from "~/features/admin/relationTypes/hooks";
import type { RelationTypeEditFormData, RelationTypeFormData } from "~/features/admin/relationTypes/types";
import { CategoryCombobox } from "./CategoryCombobox";

interface EditableRelationType {
  id: string;
  name: string;
  sourceCategoryId: string;
  targetCategoryId: string;
}

interface RelationTypeFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** If set, the dialog is in edit mode and prefills from this relation type. */
  relationType?: EditableRelationType;
}

const FORM_ID = "relation-type-form";

export function RelationTypeFormDialog({ open, onOpenChange, relationType }: RelationTypeFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(relationType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {isEdit
              ? `${t("ui.edit")} ${t("model.relationType.modelName")}`
              : `${t("ui.add")} ${t("model.relationType.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>
          {relationType ? (
            <EditBody relationType={relationType} onClose={() => onOpenChange(false)} />
          ) : (
            <CreateBody onClose={() => onOpenChange(false)} />
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-12-medium text-fg-2">{children}</span>;
}

function FormFooter({ processing }: { processing: boolean }) {
  const { t } = useTranslation();
  return (
    <DialogFooter>
      <DialogClose asChild>
        <Button type="button" variant="outline" disabled={processing}>
          {t("ui.cancel")}
        </Button>
      </DialogClose>
      <Button type="submit" form={FORM_ID} disabled={processing}>
        {processing && <Loader2 className="animate-spin" />}
        <span>{t("ui.save")}</span>
      </Button>
    </DialogFooter>
  );
}

function CreateBody({ onClose }: { onClose: () => void }) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RelationTypeFormData>({
    defaultValues: { name: "", sourceCategoryId: "", targetCategoryId: "" },
  });

  const { submit, processing } = useRelationTypeCreate({
    onSuccess: () => {
      reset();
      onClose();
    },
  });

  return (
    <>
      <form id={FORM_ID} onSubmit={handleSubmit(submit)} className="space-y-4">
        <Fields register={register} control={control} errors={errors} />
      </form>
      <FormFooter processing={processing} />
    </>
  );
}

function EditBody({ relationType, onClose }: { relationType: EditableRelationType; onClose: () => void }) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RelationTypeFormData>({
    defaultValues: {
      name: relationType.name,
      sourceCategoryId: relationType.sourceCategoryId,
      targetCategoryId: relationType.targetCategoryId,
    },
  });

  const { submit, processing } = useRelationTypeEdit({
    onSuccess: () => {
      reset();
      onClose();
    },
  });

  const onSubmit = (formData: RelationTypeFormData) => {
    const data: RelationTypeEditFormData = { ...formData, id: relationType.id };
    submit(data);
  };

  return (
    <>
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Fields register={register} control={control} errors={errors} />
      </form>
      <FormFooter processing={processing} />
    </>
  );
}

interface FieldsProps {
  register: ReturnType<typeof useForm<RelationTypeFormData>>["register"];
  control: ReturnType<typeof useForm<RelationTypeFormData>>["control"];
  errors: ReturnType<typeof useForm<RelationTypeFormData>>["formState"]["errors"];
}

function Fields({ register, control, errors }: FieldsProps) {
  const { t } = useTranslation();
  const { categories } = useRelationTypesModelContext();

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <FieldLabel>{t("model.common.name")}</FieldLabel>
        <Input {...register("name", { required: t("ui.required") })} aria-invalid={Boolean(errors.name)} />
        {errors.name && <p className="text-12-medium text-danger">{errors.name.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <FieldLabel>{t("model.relationType.sourceCategory")}</FieldLabel>
        <Controller
          name="sourceCategoryId"
          control={control}
          rules={{ required: t("ui.required") }}
          render={({ field }) => (
            <CategoryCombobox
              options={categories}
              value={field.value}
              onChange={field.onChange}
              placeholder={t("model.relationType.sourceCategory")}
              ariaInvalid={Boolean(errors.sourceCategoryId)}
            />
          )}
        />
        {errors.sourceCategoryId && <p className="text-12-medium text-danger">{errors.sourceCategoryId.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <FieldLabel>{t("model.relationType.targetCategory")}</FieldLabel>
        <Controller
          name="targetCategoryId"
          control={control}
          rules={{ required: t("ui.required") }}
          render={({ field }) => (
            <CategoryCombobox
              options={categories}
              value={field.value}
              onChange={field.onChange}
              placeholder={t("model.relationType.targetCategory")}
              ariaInvalid={Boolean(errors.targetCategoryId)}
            />
          )}
        />
        {errors.targetCategoryId && <p className="text-12-medium text-danger">{errors.targetCategoryId.message}</p>}
      </div>
    </>
  );
}
